import { useState } from 'react'

import {
  Box,
  Text,
  Title,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import MoneyField from 'modules/core/components/MoneyField'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { HealthcarePlanData } from '../types'
import { employeeCostStepSchema } from '../validation'

const EmployeeCostStep = ({
  data,
  onDataChanged,
  locale,
  currency,
}: WizardStepRenderParams<HealthcarePlanData> & { locale: string; currency: string }) => {
  // Track which fields have been touched/blurred to show validation errors
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set())

  // Helper to mark field as touched
  const markFieldTouched = (fieldName: string) => {
    setTouchedFields((prev) => new Set(prev).add(fieldName))
  }

  // Use Zod validation from validation.ts
  const validationResult = employeeCostStepSchema(locale, currency).safeParse(data)

  // Extract field-specific errors from Zod validation, but only show if field was touched
  const getFieldError = (fieldName: string): string | undefined => {
    if (validationResult.success || !touchedFields.has(fieldName)) return undefined

    const fieldError = validationResult.error.errors.find((error) => error.path.includes(fieldName))
    return fieldError?.message
  }

  const errors = {
    employeeCostCents: getFieldError('employeeCostCents'),
    coveredEmployeeCostCents: getFieldError('coveredEmployeeCostCents'),
  }

  const totalCostEuros =
    data.employeeCostCents === undefined ? undefined : fromCents(data.employeeCostCents)
  const companyCostEuros =
    data.coveredEmployeeCostCents === undefined
      ? undefined
      : fromCents(data.coveredEmployeeCostCents)
  const employeeCostEuros =
    totalCostEuros === undefined || companyCostEuros === undefined
      ? undefined
      : Math.max(0, totalCostEuros - companyCostEuros)

  return (
    <Box flexDirection="column" gap="s24" width="s360">
      <Box flexDirection="column" gap="s8">
        <Title
          title={i18n.t('benefits.healthcare.plan_wizard.employee_cost_step.title')}
          description={i18n.t('benefits.healthcare.plan_wizard.employee_cost_step.description')}
        />
      </Box>

      <Box flexDirection="column" gap="s24">
        <MoneyField
          label={i18n.t('benefits.healthcare.plan_wizard.employee_cost_step.total_cost.label')}
          mandatory
          currency={currency}
          value={totalCostEuros !== undefined ? String(totalCostEuros) : ''}
          onChange={(value: string) => {
            const cents = value.length > 0 ? toCents(Number(value)) : undefined
            // In most situations dependents cost the same as employees
            onDataChanged({
              ...data,
              employeeCostCents: cents,
              dependentCostCents: cents,
            })
          }}
          onBlur={() => markFieldTouched('employeeCostCents')}
          placeholder="0"
          error={errors.employeeCostCents}
        />

        <Box gap="s8">
          <Text size="200" weight="semibold" color="grey800">
            {i18n.t('common.paid_by')}
          </Text>
          <Box
            flexDirection="row"
            gap="s16"
            border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
            borderRadius={{ all: 'abs016' }}
            padding="s16"
          >
            <Box flexGrow>
              <MoneyField
                label={i18n.t('common.company')}
                mandatory
                currency={currency}
                value={companyCostEuros !== undefined ? String(companyCostEuros) : ''}
                onChange={(value: string) => {
                  onDataChanged({
                    ...data,
                    coveredEmployeeCostCents: value.length > 0 ? toCents(Number(value)) : undefined,
                  })
                }}
                onBlur={() => markFieldTouched('coveredEmployeeCostCents')}
                placeholder="0"
                error={errors.coveredEmployeeCostCents}
              />
            </Box>

            <Box flexGrow>
              <MoneyField
                label={i18n.t('common.employee')}
                currency={currency}
                value={employeeCostEuros ? String(employeeCostEuros) : ''}
                onChange={() => {}} // Read-only, calculated field
                placeholder="0"
                readonly
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {employeeCostEuros !== undefined && employeeCostEuros > 0 && (
        <Callout
          type="info"
          title={i18n.t('benefits.healthcare.plan_wizard.employee_cost_step.callout.title')}
          description={i18n.t(
            'benefits.healthcare.plan_wizard.employee_cost_step.callout.description',
            {
              amount: MoneyWithCurrency({
                amount: toCents(employeeCostEuros),
                currency,
                locale,
              }),
            }
          )}
        />
      )}
    </Box>
  )
}

// Define step as factory function (like working wizards) instead of direct object
export const employeeCostStep = (
  locale: string,
  currency: string
): WizardStep<HealthcarePlanData> => ({
  title: i18n.t('benefits.healthcare.plan_wizard.employee_cost_step.title'),
  render: (props) => <EmployeeCostStep {...props} locale={locale} currency={currency} />,
  nextText: i18n.t('common.continue'),
  isCompleted: (data: HealthcarePlanData) => {
    // Use Zod validation for completion check
    const validationResult = employeeCostStepSchema(locale, currency).safeParse(data)
    return validationResult.success
  },
})
